import Paginacao from "../types/paginacao";
import User from "../types/user";
import warehouseClient from "../types/warehouseClient";
import api, { fetchData } from "./api";

export interface UsuarioApiResponse {
    data: UsuarioResponse
    success: boolean;
    message?: string;
}

export interface UsuarioResponse {
    id: number;
    email: string;
    fullName: string;
    username: string;
    accountExpired: boolean,
    accountLocked: boolean,
    passwordExpired: boolean,
    enabled: boolean,
    secUserSecRoles: SecUserSecRole[],
    worksTo: warehouseClient[]
}

export interface UsuarioVM {

}

export interface SecUserSecRole {
    secRole: {
      id: number;
      authority: string;
    };
  }

export interface ApiValidationError {
    type: string;
    title: string;
    status: number;
    errors: Record<string, string[]>;
    traceId: string;
}


/** Formato padrão de retorno dos endpoints que devolvem data + messages. */
export interface ApiResultVM<T> {
    data: T | null;
    messages: string[];
}

/** Dados do usuário dono do token do link de troca de senha. */
export interface TokenRedefinicaoSenhaResponse {
    username: string;
    fullName: string;
}

/** Dados enviados pela tela pública de troca de senha. */
export interface RedefinicaoSenhaVM {
    token: string;
    novaSenha: string;
    confirmaNovaSenha: string;
}

/** Dados enviados pelo formulário de "esqueci minha senha", na tela de login. */
export interface SolicitacaoRedefinicaoSenhaVM {
    usuarioOuEmail: string;
}

export interface UsuarioApiCreateResponse {
    data: UsuarioResponse;
    success: boolean;
    message?: string;
    /** Mensagens da API, incluindo o resultado do envio do e-mail de acesso. */
    messages?: string[];
    errors?: Record<string, string[]>;
}

export interface UsuarioPostVM {
    id: number;
    fullName: string;
    userName: string;
    email: string;
    password: string;
    enabled: boolean;
    accountExpired: boolean;
    accountLocked: boolean;
    passwordExpired: boolean;
    confirmPassword: string;
    roleIds: number[];
    wareHouseClientIds: number[];
}

/**
 * Atualizacao de cadastro do usuario. Nao carrega senha de proposito:
 * a troca de senha tem fluxos proprios (updatePassword e resetPassword).
 */
export interface UsuarioPutVM {
    id: number;
    userName?: string;
    email?: string;
    fullName?: string;
    enabled?: boolean;
    roleIds?: number[];
    wareHouseClientIds?: number[];
    accountExpired?: boolean;
    accountLocked?: boolean;
    passwordExpired?: boolean;
}

/**
 * Flags de exibição da listagem de usuários. Sem nenhuma flag habilitada a API
 * retorna apenas os usuários ativos, com a conta não expirada e não bloqueada.
 */
export interface FiltroUsuario {
    mostrarDesativados?: boolean;
    mostrarExpirados?: boolean;
    mostrarBloqueados?: boolean;
}

/**
 * Senha padrão sugerida no cadastro de novos usuários.
 *
 * IMPORTANTE: precisa ser igual a GiantLogModel.SenhaPadrao.Valor no back-end.
 * É esse valor que faz o login exigir a troca de senha no primeiro acesso; se os
 * dois saírem de sincronia, o usuário novo entra sem ser obrigado a trocar.
 */
export const SENHA_PADRAO = "Giant@2026!";

/** Filtro que traz todos os usuários, independente da situação da conta. */
export const FILTRO_TODOS_OS_USUARIOS: FiltroUsuario = {
    mostrarDesativados: true,
    mostrarExpirados: true,
    mostrarBloqueados: true,
};

export const getListaUsuarios = async (filtro?: FiltroUsuario) => {
    try {
        const endpoint: string = "/Usuario/all";
        const response = await fetchData<User>(endpoint, filtro ?? {});
        return response.data;
    } catch (error) {
        console.error("Erro ao buscar lista de usuários:", error);
        throw error;
    }
};

export const getUsuarios = async () => {
    try {
        const endpoint: string = "/Usuario/all";
        const response = await fetchData<User>(endpoint, {});
        return response
    } catch (error) {
        console.error("Erro ao buscar lista de usuários:", error);
        throw error;
    }
};

export const getUsuariosPaginated = async (
    paginacao: Paginacao
  ) => {
    try {
      const endpoint: string = '/Usuario/all/paginado';
      const params: Record<string, any> = {
        pageNum: paginacao.pageNum + 1,
        quantityPerPage: paginacao.quantityPerPage,
      };

      // Adicionar searchTerm se existir
      if (paginacao.searchTerm && paginacao.searchTerm.trim() !== '') {
        params.searchTerm = paginacao.searchTerm.trim();
      }

      // Adicionar sortModel se existir
      if (paginacao.sortModel && paginacao.sortModel.length > 0) {
        params.sortModel = paginacao.sortModel;
      }

      // Adicionar filterModel se existir
      if (paginacao.filterModel && paginacao.filterModel.length > 0) {
        params.filterModel = { items: paginacao.filterModel };
      }

      const response = await fetchData<User>(endpoint, params);

      return response;
    } catch (error) {
      console.error('Erro ao buscar lista de usuários:', error);
      throw error;
    }
};

export const getUsuarioById = async (id: number) => {
    try {
        const response = await api.get<UsuarioApiResponse>(`/Usuario/${id}`);
        return response.data; 
    } catch (error) {
        console.error(`Erro ao buscar usuário com ID ${id}`, error);
        throw error;
    }
};

export const updatePasswordService = async (currentPassword: string, newPassword: string,
    usuarioId: number): Promise<UsuarioApiResponse> => {
    try {
        const endpoint: string = "/Usuario/updatePassword";
        const body = {
            currentPassword: currentPassword,
            newPassword: newPassword, 
            usuarioId: usuarioId,
        };

        const response = await api.put<UsuarioApiResponse>(endpoint, body);

        return response.data;
    } catch (error) {
        console.error("Erro ao atualizar a senha do usuário:", error);
        throw error;
    }
};

export const createUsuario = async (usuario: UsuarioVM) => {
    try {
        const response = await api.post<UsuarioApiCreateResponse>("/Usuario", usuario);
        return response.data;
    } catch (error) {
        console.error("Erro ao criar usuário:", error);
        throw error;
    }
};

export const updateUsuario = async (id: number, usuario: UsuarioPutVM) => {
    try {
        const response = await api.put<UsuarioApiResponse>(`/Usuario/update/${id}`, usuario);
        return response.data;
    } catch (error) {
        console.error(`Erro ao atualizar usuário com ID ${id}:`, error);
        throw error;
    }
};

/**
 * Reseta a senha do usuário para a senha padrão e dispara o e-mail com o link de troca de senha.
 */
export const resetPasswordService = async (id: number) => {
    try {
        const response = await api.put<ApiResultVM<string>>(`/Usuario/resetPassword/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Erro ao resetar a senha do usuário com ID ${id}:`, error);
        throw error;
    }
};

/**
 * Valida o token recebido no link do e-mail e retorna os dados do usuário correspondente.
 */
export const validarTokenRedefinicaoSenha = async (token: string) => {
    const response = await api.get<ApiResultVM<TokenRedefinicaoSenhaResponse>>(
        "/RedefinicaoSenha/validar",
        { params: { token } }
    );
    return response.data;
};

/**
 * Grava a nova senha escolhida pelo usuário na tela de troca de senha.
 */
export const redefinirSenhaComToken = async (dados: RedefinicaoSenhaVM) => {
    const response = await api.post<ApiResultVM<string>>("/RedefinicaoSenha/redefinir", dados);
    return response.data;
};

/**
 * Solicita o link de redefinição de senha ("esqueci minha senha"), a partir da tela de
 * login. A resposta é sempre a mesma mensagem genérica, exista ou não o usuário/e-mail
 * informado - de propósito, ver RedefinicaoSenhaController.Solicitar no back-end.
 */
export const solicitarRedefinicaoSenha = async (dados: SolicitacaoRedefinicaoSenhaVM) => {
    const response = await api.post<ApiResultVM<string>>("/RedefinicaoSenha/solicitar", dados);
    return response.data;
};

export const deleteUsuario = async (id: number) => {
    try {
        await api.delete(`/Usuario/${id}`);
        return true;
    } catch (error) {
        console.error(`Erro ao excluir usuário com ID ${id}:`, error);
        return false;
    }
};
